import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from "react";
import en, { type Key } from "./en";
import nl from "./nl";

export type Lang = "en" | "nl";
const tables: Record<Lang, Record<Key, string>> = { en, nl };
type Params = Record<string, string | number | null | undefined>;
export type T = (key: Key | string, params?: Params) => string;

function detect(): Lang {
  try {
    const saved = localStorage.getItem("dt.lang");
    if (saved === "en" || saved === "nl") return saved;
  } catch {
    /* storage may be blocked */
  }
  return navigator.language?.toLowerCase().startsWith("nl") ? "nl" : "en";
}

const Ctx = createContext<{ lang: Lang; setLang: (l: Lang) => void; t: T }>({
  lang: "en",
  setLang: () => {},
  t: (k) => k,
});

export function I18nProvider({ children }: { children: ReactNode }) {
  const [lang, setLangState] = useState<Lang>(detect);
  const setLang = useCallback((l: Lang) => {
    setLangState(l);
    document.documentElement.lang = l;
    try {
      localStorage.setItem("dt.lang", l);
    } catch {
      /* ignore */
    }
  }, []);
  const t = useCallback<T>(
    (key, params) => {
      const s = (tables[lang] as Record<string, string>)[key] ?? (en as Record<string, string>)[key] ?? key;
      if (!params) return s;
      return s.replace(/\{(\w+)\}/g, (_, name) => String(params[name] ?? ""));
    },
    [lang],
  );
  const value = useMemo(() => ({ lang, setLang, t }), [lang, setLang, t]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export const useI18n = () => useContext(Ctx);
export const useT = () => useContext(Ctx).t;
